import React, { useEffect, useMemo, useState } from 'react';

import Tooltip from 'rc-tooltip';
import classNames from 'classnames';

import { ColumnDef } from '@tanstack/react-table';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWalking } from '@fortawesome/pro-solid-svg-icons';
import { faBullseye } from '@fortawesome/pro-regular-svg-icons';

import Table from 'Components/Table';
import type { TablePaginationConfig, TableRowSelection } from 'Components/Table/types';

import { FlexBox, Text } from '@eltoro-ui/components';
import { Loading } from 'Components';
import AudienceStatusAccessor from 'Pages/SavedAudiences/AudienceStatusAccessor';
import AudienceEmptyStates from 'Pages/CampaignCreation/components/CampaignSetup/AudiencesWithGoals/AudienceEmptyStates';

import { getCampaignAudiencesByGoal } from 'Requests/Request_Methods/campaignMethods';

import { getFileName } from 'Helpers';

import { AudienceStatusEnum, GoalSectionTitles } from 'enums';
import type { TTarget, TTargets } from 'types';

type AudienceTableBySelectedGoalProps = {
  goal: GoalSectionTitles;
  campaignId: string | number | undefined;
  selectedAudiences: TTargets;
  onSelect: (audienceList: TTarget[], goal: GoalSectionTitles) => void;
  initialData: {
    campaign_id: string;
    targets: TTargets;
    total: number;
    total_pages: number;
    current_page: number;
  };
};

export default function AudienceTableBySelectedGoal({
  goal,
  campaignId,
  selectedAudiences,
  onSelect,
  initialData,
}: AudienceTableBySelectedGoalProps) {
  const [selectedTargets, setSelectedTargets] = useState<TTarget[]>(selectedAudiences);

  const [isLoading, setIsLoading] = useState(false);
  const [audiencesByGoal, setAudiencesByGoal] = useState({
    [goal]: initialData,
  });

  const campaignAudiencesesByGoal = (id: string, goal: GoalSectionTitles) => {
    setIsLoading(true);
    getCampaignAudiencesByGoal(goal, id, audiencesByGoal[goal]?.current_page, 10).then(res => {
      if (res.data) {
        setAudiencesByGoal(prevState => {
          if (res?.data) {
            return { ...prevState, [goal]: { ...res?.data } };
          }
          return prevState;
        });
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (campaignId) campaignAudiencesesByGoal(`${campaignId}`, goal);
  }, [audiencesByGoal[goal]?.current_page, campaignId]);

  useEffect(() => {
    onSelect(selectedTargets, goal);
  }, [selectedTargets]);

  const rowSelection = useMemo<TableRowSelection<TTarget>>(
    () => ({
      selectedRowKeys: selectedAudiences.map(({ id }) => id),
      onChange: (_, selectedRows) => {
        setSelectedTargets(prevTargets => {
          const filteredRows = prevTargets.filter(
            row => audiencesByGoal[goal].targets.findIndex(({ id }) => id === row.id) === -1
          );
          return [...filteredRows, ...selectedRows];
        });
      },
      checkboxRender: (originalNode, row) => {
        if (row.status === AudienceStatusEnum.IN_USE) {
          const overlay = (
            <FlexBox flexDirection="column" gap="0.5rem">
              <span>
                This audience is already part of an active ad campaign, {row.campaign_name_in_use},
                and it can't be used in a new campaign.
              </span>
              <span>
                You can either edit the {row.campaign_name_in_use} campaign to add creatives or wait
                for it to finish before sending new ads.
              </span>
            </FlexBox>
          );
          return (
            <Tooltip
              placement="topLeft"
              trigger="hover"
              classNames={{ root: classNames('audience-tooltip', 'audience-disable-in_use') }}
              showArrow
              align={{
                offset: [-15, -3],
                targetOffset: [-6, 0],
              }}
              overlay={overlay}
            >
              <span>{originalNode}</span>
            </Tooltip>
          );
        }

        if (row.status === AudienceStatusEnum.PENDING)
          return (
            <Tooltip
              placement="topLeft"
              trigger="hover"
              classNames={{ root: classNames('audience-tooltip', 'audience-disable-pending') }}
              showArrow
              align={{
                offset: [-15, -3],
                targetOffset: [-6, 0],
              }}
              overlay="Your audience creation is in process. Please refresh after a few minutes."
            >
              <span>{originalNode}</span>
            </Tooltip>
          );

        return originalNode;
      },
    }),
    [selectedAudiences, audiencesByGoal[goal].targets]
  );

  const tablePagination = useMemo<TablePaginationConfig>(
    () => ({
      hideOnSinglePage: true,
      pageSize: 10,
      current: audiencesByGoal[goal]?.current_page,
      total: audiencesByGoal[goal]?.total,
      totalPages: audiencesByGoal[goal]?.total_pages,
      onChange: page =>
        setAudiencesByGoal(prev => {
          return {
            ...prev,
            [goal]: {
              ...prev[goal],
              current_page: page,
            },
          };
        }),
    }),
    [
      audiencesByGoal[goal]?.current_page,
      audiencesByGoal[goal]?.total,
      audiencesByGoal[goal]?.total_pages,
    ]
  );

  const columns: ColumnDef<TTarget>[] = [
    {
      header: 'Source',
      accessorKey: 'source',
      size: 76,
      enableResizing: false,
      enableSorting: false,
      cell: ({ row: { original: audience } }) => (
        <FlexBox justifyContent="center" alignItems="center">
          <FontAwesomeIcon
            icon={faBullseye}
            color={
              audience.status !== AudienceStatusEnum.ACTIVE
                ? '#D1D1D1'
                : 'var(--color-secondary-300)'
            }
            className="table-audience-icon-small"
          />
        </FlexBox>
      ),
    },
    {
      header: 'Name',
      accessorKey: 'name',
      enableSorting: false,
      cell: ({ row: { original: audience } }) => (
        <Text on="white" size="m" weight="bold" tag="div" UNSAFE_className="table-audience-name">
          {audience.type === 'prospects' ? getFileName(audience.name) : audience.name}
          <br />
          <p
            style={{
              fontWeight: 300,
              fontSize: '14px',
              margin: '0',
              color: '#6D6D6D',
              textTransform: 'capitalize',
            }}
          >
            {audience.source}
          </p>
        </Text>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableSorting: false,
      enableResizing: false,
      size: 136,
      cell: ({ row: { original: audience } }) =>
        audience.status ? <AudienceStatusAccessor status={audience.status} /> : '-',
    },
    {
      header: 'Audience Size',
      accessorKey: 'prospects_counts',
      enableSorting: false,
      enableResizing: false,
      size: 180,
      cell: ({ row: { original: audience } }) => (
        <FlexBox alignItems="center" gap="10px">
          <Text on="white" size="l" weight="bold" UNSAFE_className="prospects_count">
            <FontAwesomeIcon
              icon={faWalking}
              color={
                audience.status !== AudienceStatusEnum.ACTIVE
                  ? '#D1D1D1'
                  : 'var(--color-secondary-300)'
              }
            />{' '}
            {audience.prospects_counts || '-'}
          </Text>
          <span className="prospect-info">PROSPECTS</span>
        </FlexBox>
      ),
    },
  ];

  const content = () => {
    if (isLoading) {
      return <Loading />;
    }
    if (audiencesByGoal[goal].targets?.length) {
      return (
        <Table<TTarget>
          showHeader={false}
          data={audiencesByGoal[goal].targets}
          columns={columns}
          rowDisabled={audience => audience.status !== AudienceStatusEnum.ACTIVE}
          rowSelection={rowSelection}
          pagination={tablePagination}
        />
      );
    }
    return <AudienceEmptyStates goal={goal} />;
  };

  return <div>{content()}</div>;
}
